import classnames from 'classnames';
import * as React from 'react';

import { Button } from '@ao-internal/components-react';
import * as digitalData from '@ao-internal/digital-data';

import { IconClassMap, IconTypes, TaggingEventNames } from '../../globals';
import useChat from '../../hooks/useChat';
import {
	ChatMessageTypes,
	ChatMessageTypesClassMap,
	InputTypes,
} from '../../store';

export interface ButtonMessageContent {
	displayText: string;
	replyText: string;
	count?: number;
	iconType?: IconTypes;
}

interface ButtonMessageProps {
	groupIntent?: string;
	buttons: ButtonMessageContent[];
	isReadOnly?: boolean;
}

export const dataTestId = `${
	ChatMessageTypesClassMap[ChatMessageTypes.Button]
}-message`;

export const ButtonMessage: React.FC<ButtonMessageProps> = ({
	groupIntent,
	buttons,
	isReadOnly,
}) => {
	const { sendMessage } = useChat();
	const buttonsTotal = buttons.length;

	const handleClick = (evt: React.MouseEvent, index: number) => {
		const targetButton = buttons[index];

		digitalData.sendBeacon(TaggingEventNames.interaction, {
			type: 'click',
			element: 'button-message',
			value: targetButton.replyText,
		});

		sendMessage(targetButton.replyText, {
			inputType: InputTypes.Interaction,
			groupIntent,
		});
	};

	return (
		<div
			ref={(node) => node && isReadOnly && node.setAttribute('inert', '')}
			data-testid={dataTestId}
			className="mx-2 mb-2"
			data-tag-area="button message"
		>
			<div className="flex flex-wrap -m-1">
				{buttons.map((button: ButtonMessageContent, index: number) => {
					const { iconType, displayText } = button;
					const position = index + 1;
					const iconClass =
						iconType !== undefined ? IconClassMap[iconType] : false;

					return (
						<div
							// eslint-disable-next-line react/no-array-index-key
							key={`button-${index}`}
							className={classnames('py-1 px-1', {
								'w-full': buttonsTotal === 1,
								'w-1/2': buttonsTotal >= 2,
							})}
						>
							{iconClass ? (
								<Button
									data-tag-name="link button"
									data-tag-position={position}
									data-tag-icon
									type="button"
									variant="secondary"
									onClick={(evt: React.MouseEvent) => handleClick(evt, index)}
									className="hover:text-white hover:bg-cta-actionhover justify-start w-full pr-0 whitespace-normal bg-white"
								>
									<span
										className={`ico ico-${iconClass} mr-2 inline-flex text-lg leading-none`}
									/>
									{displayText}
								</Button>
							) : (
								<Button
									data-tag-name="link button"
									data-tag-position={position}
									type="button"
									variant="secondary"
									onClick={(evt: React.MouseEvent) => handleClick(evt, index)}
									className="hover:bg-cta-actionhover hover:text-white px-0 whitespace-normal bg-white"
									fullWidth
								>
									{displayText}
								</Button>
							)}
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default ButtonMessage;
